/**
 * Try again before giving up.
 *
 * Written after a single refused request left the Miro panel permanently
 * half-broken: its filter rows vanished and never came back, because the tags
 * are fetched once when the panel mounts and the panel does not remount when
 * it is closed and reopened — Miro keeps the iframe alive. One transient
 * failure, and the only way back was a full board reload.
 *
 * So the rule here is not "retry because networks are flaky". It is: anything
 * fetched exactly once, in a component that may live for hours, has to survive
 * that one fetch failing.
 */

/**
 * Three further goes, spread over about four seconds.
 *
 * Sized against what actually fails here: a panel iframe that Miro has only
 * just built, whose channel to the board takes a moment to come up. Two quick
 * retries were not enough to outlast that.
 */
const DELAYS_MS = [300, 900, 2500]

export async function withRetry<T>(
  work: () => Promise<T>,
  attempts = DELAYS_MS.length + 1,
): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await work()
    } catch (error) {
      lastError = error
      const wait = DELAYS_MS[attempt]
      // Spaced out rather than immediate: whatever refused the first request is
      // unlikely to have changed its mind a millisecond later.
      if (wait !== undefined) await new Promise((resolve) => setTimeout(resolve, wait))
    }
  }
  throw lastError
}
